import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Shield, Brain, Lock, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StickyScrollingCards } from './ui/sticky-scrolling-cards';

gsap.registerPlugin(ScrollTrigger);

const phases = [
  {
    num: '01',
    icon: Shield,
    title: 'PERSONALIZED ASSESSMENT',
    description: 'AI-driven survey adapts to your role - parent, student, institution, or business - for tailored cybersecurity insights.',
    link: '/security-survey'
  },
  {
    num: '02',
    icon: Brain,
    title: 'SMART ANALYSIS',
    description: 'Advanced AI evaluates your responses to identify potential security risks and areas for improvement.',
    link: '/security-survey'
  },
  {
    num: '03',
    icon: Lock,
    title: 'SECURITY RECOMMENDATIONS',
    description: 'Receive customized security tips and actionable steps to enhance your digital protection.',
    link: '/security-survey'
  },
  {
    num: '04',
    icon: Award,
    title: 'DIGITAL CERTIFICATE',
    description: 'Earn a Smile Cyber Tech Survey Certification recognizing your commitment to online safety.',
    link: '/security-survey'
  },
];

const PhaseCard = ({ phase }: { phase: typeof phases[0] }) => {
  const Icon = phase.icon;
  return (
    <div className="flex flex-col gap-6 justify-center w-full bg-[#f8f9fa] rounded-3xl pb-4">
      {/* Large Illustration Box (Transparent/Floating in Light Theme) */}
      <div className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto lg:h-[280px] xl:h-[320px] rounded-3xl flex items-center justify-center relative group">
        
        {/* Center Icon (Light line art style) */}
        <div className="relative z-10 flex items-center justify-center transition-all duration-500 group-hover:scale-105">
          <Icon className="w-32 h-32 sm:w-40 sm:h-40 text-[#efae00] transition-transform duration-500 group-hover:-rotate-3" strokeWidth={1} />
        </div>

      </div>

      {/* Text Content */}
      <div className="flex flex-col gap-3 px-2">
        <div className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase">
          <span className="text-[#efae00]">{phase.num}</span>
          <span className="w-1 h-1 rounded-full bg-[#efae00]" />
          <span className="text-slate-400">CAPABILITIES</span>
        </div>
        
        <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0a192f] tracking-tight">
          {phase.title}
        </h3>
        
        <p className="text-slate-500 text-base sm:text-lg leading-relaxed max-w-2xl">
          {phase.description}
        </p>

        <Link 
          to={phase.link}
          className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[#0a192f] hover:text-[#efae00] transition-colors group w-fit px-5 py-2.5 rounded-full border border-slate-300 hover:border-[#efae00] bg-white shadow-sm"
        >
          Know More
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

const SurveyCapabilitiesSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    // We use gsap.matchMedia to handle responsive animations cleanly
    let ctx = gsap.matchMedia();

    ctx.add("(min-width: 1024px)", () => {
      // DESKTOP: Pinned Scroll Animation
      const cards = cardsRef.current;
      if (!cards.length || !sectionRef.current || !leftRef.current) return;

      // Initial states
      gsap.set(leftRef.current, { opacity: 0, y: 30 });
      
      // Animate the left side in when the section comes into view
      gsap.to(leftRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
        }
      });

      // Prepare cards: First card visible, rest hidden and shifted down
      gsap.set(cards.slice(1), { autoAlpha: 0, y: 50 });
      gsap.set(cards[0], { autoAlpha: 1, y: 0 });

      // Create a timeline that pins the section
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "center center",
          end: `+=${cards.length * 100}%`, // Scroll distance based on number of phases
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        }
      });

      // Loop through cards to build the cross-fade sequence
      cards.forEach((card, index) => {
        if (index === 0) return; // Skip the first one as it's our starting point
        
        const prevCard = cards[index - 1];
        
        // At 'step{index}' label, animate previous card out and current card in simultaneously
        tl.to(prevCard, { autoAlpha: 0, y: -50, duration: 1, ease: "power2.inOut" }, `step${index}`)
          .to(card, { autoAlpha: 1, y: 0, duration: 1, ease: "power2.inOut" }, `step${index}`);
      });
    });

    ctx.add("(max-width: 1023px)", () => {
       // MOBILE/TABLET: Standard scrolling (No Pin)
       const cards = cardsRef.current;
       
       // Reset properties in case of window resize
       gsap.set(leftRef.current, { opacity: 1, y: 0, clearProps: "all" });
       gsap.set(cards, { clearProps: "all" });

       // Left side simple fade-in
       gsap.fromTo(leftRef.current, 
         { opacity: 0, y: 30 },
         { 
           opacity: 1, 
           y: 0, 
           duration: 0.8,
           scrollTrigger: {
             trigger: leftRef.current,
             start: "top 80%",
           }
         }
       );

       // Cards simple fade-in sequentially on scroll
       cards.forEach((card) => {
         gsap.fromTo(card, 
           { autoAlpha: 0, y: 40 },
           { 
             autoAlpha: 1, 
             y: 0, 
             duration: 0.6,
             scrollTrigger: {
               trigger: card,
               start: "top 85%",
             }
           }
         );
       });
    });

    return () => ctx.revert(); // Cleanup GSAP contexts and scroll triggers on unmount
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative py-24 text-slate-900 w-full flex items-center min-h-[80vh] bg-[#f8f9fa]"
    >
      {/* Background Noise Texture */}
      <div 
        className="absolute inset-0 z-0 pointer-events-none mix-blend-overlay opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />
      
      {/* 3D Decorative Glowing Orbs Removed */}
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center">
          
          {/* LEFT SIDE: Static Info (Stays put while right side animates on desktop) */}
          <div className="w-full lg:w-5/12 xl:w-1/3" ref={leftRef}>
            <div>
              <h2 className="text-5xl md:text-6xl font-extrabold mb-4 tracking-tight leading-[1.1] text-[#0a192f]">
                Our Capabilities<br />
                <span className="text-[#efae00]">
                  & Expertise
                </span>
              </h2>
              <p className="text-lg text-slate-500 font-light mt-6 leading-relaxed">
                Transforming businesses through innovative AI solutions that drive growth and efficiency.
              </p>

              <div className="mt-10">
                <Link
                  to="/security-survey"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#efae00] text-[#030712] font-bold text-base rounded-full hover:bg-[#f5c842] transition-all duration-300 shadow-[0_0_30px_rgba(239,174,0,0.25)] hover:-translate-y-0.5 group"
                >
                  <span>Start Survey Now</span>
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE DESKTOP: Animated Cards Container */}
          <div className="hidden lg:block w-full lg:w-7/12 xl:w-2/3 relative h-[500px] xl:h-[550px]" ref={rightColumnRef}>
            {phases.map((phase, index) => (
              <div 
                key={index}
                ref={el => cardsRef.current[index] = el}
                className="absolute inset-0 flex flex-col gap-6 justify-center"
              >
                <PhaseCard phase={phase} />
              </div>
            ))}
          </div>

          {/* RIGHT SIDE MOBILE: Sticky Scrolling Cards */}
          <div className="block lg:hidden w-full mt-16">
            <StickyScrollingCards items={phases.map(phase => <PhaseCard phase={phase} />)} />
          </div>

        </div>
      </div>
    </section>
  );
};

export default SurveyCapabilitiesSection;
